import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  Database, HardDrive, FileDown, Clock, Shield, RefreshCw, Download,
  Minus, Plus, Save, Check
} from 'lucide-react';

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DataManagement = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [retentionEdits, setRetentionEdits] = useState({});
  const [retentionSaving, setRetentionSaving] = useState({});
  const [retentionSaved, setRetentionSaved] = useState({});

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings/data/stats');
      if (res.data?.success) setStats(res.data.data);
    } catch (err) {
      console.error('Failed to load data stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (selectedCollections.length === 0) {
      toast.error('Select at least one collection to export');
      return;
    }
    try {
      setExporting(true);
      const res = await api.post('/admin/settings/data/export', { format, collections: selectedCollections });
      if (res.data?.success) {
        toast.success(`Export queued (${format.toUpperCase()}). You will be notified when ready.`);
        setSelectedCollections([]);
      }
    } catch (err) {
      toast.error('Failed to initiate export');
    } finally {
      setExporting(false);
    }
  };

  const toggleCollection = (name) => {
    setSelectedCollections(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const adjustRetention = (configKey, currentYears, minimumYears, delta) => {
    const current = retentionEdits[configKey] ?? currentYears;
    const next = Math.max(minimumYears || 1, current + delta);
    setRetentionEdits(prev => ({ ...prev, [configKey]: next }));
  };

  const saveRetention = async (configKey) => {
    const years = retentionEdits[configKey];
    if (years === undefined) return;
    setRetentionSaving(prev => ({ ...prev, [configKey]: true }));
    try {
      const res = await api.put('/admin/settings/data/retention', { key: configKey, years });
      if (res.data?.success) {
        toast.success(res.data.message);
        setRetentionEdits(prev => { const n = { ...prev }; delete n[configKey]; return n; });
        setRetentionSaved(prev => ({ ...prev, [configKey]: true }));
        setTimeout(() => setRetentionSaved(prev => ({ ...prev, [configKey]: false })), 2000);
        loadStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update retention');
    } finally {
      setRetentionSaving(prev => ({ ...prev, [configKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="h-8 bg-slate-200 rounded w-1/3 animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-2xl font-extrabold text-slate-900 tracking-tight">Data Management</h2>
          <p className="text-slate-500 text-sm mt-1">Backup, export, and retention policies</p>
        </div>
        <button onClick={loadStats} className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Database Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
            <Database className="w-5 h-5" />
          </div>
          <p className="text-2xl font-extrabold text-slate-900">{stats?.databaseName || 'N/A'}</p>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Database</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-teal-50 text-teal-600">
            <HardDrive className="w-5 h-5" />
          </div>
          <p className="text-2xl font-extrabold text-slate-900">{formatBytes(stats?.totalSizeBytes)}</p>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Data Size</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-purple-50 text-purple-600">
            <FileDown className="w-5 h-5" />
          </div>
          <p className="text-2xl font-extrabold text-slate-900">{(stats?.totalDocuments || 0).toLocaleString()}</p>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-1">Total Documents</p>
        </div>
      </div>

      {/* Collections Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-lg text-slate-900">Collections</h3>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('json')}
              disabled={exporting || selectedCollections.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-primary text-white disabled:opacity-40 hover:bg-primary/90 transition-all"
            >
              <Download className="w-3 h-3" /> Export JSON
            </button>
            <button
              onClick={() => handleExport('csv')}
              disabled={exporting || selectedCollections.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-600 disabled:opacity-40 hover:bg-slate-200 transition-all"
            >
              <Download className="w-3 h-3" /> Export CSV
            </button>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {(stats?.collections || []).map((coll, i) => (
            <label key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={selectedCollections.includes(coll.collection)}
                onChange={() => toggleCollection(coll.collection)}
                className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">{coll.name}</p>
                <p className="text-xs text-slate-500">{coll.collection}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-slate-900">{coll.count.toLocaleString()}</p>
                <p className="text-xs text-slate-500">{formatBytes(coll.sizeBytes)}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Retention Policies */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" /> Data Retention Policies
        </h3>
        {(stats?.retentionPolicies || []).length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">No retention policy data available.</p>
        ) : (
          <div className="space-y-3">
            {(stats?.retentionPolicies || []).map((policy, i) => {
              const editValue = retentionEdits[policy.configKey];
              const displayYears = editValue ?? policy.retentionYears;
              const isDirty = editValue !== undefined && editValue !== policy.retentionYears;
              const isSaving = retentionSaving[policy.configKey];
              const isSaved = retentionSaved[policy.configKey];
              return (
                <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{policy.type}</p>
                    <p className="text-xs text-slate-500">{policy.basis}</p>
                    {policy.oldestRecord && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Oldest record: {new Date(policy.oldestRecord).toLocaleDateString()}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">
                      Regulatory minimum: {policy.minimumYears || 6} years
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => adjustRetention(policy.configKey, policy.retentionYears, policy.minimumYears, -1)}
                      disabled={displayYears <= (policy.minimumYears || 1)}
                      className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30 transition-all"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <div className="w-16 text-center">
                      <p className="text-lg font-extrabold text-primary leading-none">{displayYears}</p>
                      <p className="text-xs text-slate-500 font-bold">years</p>
                    </div>
                    <button
                      onClick={() => adjustRetention(policy.configKey, policy.retentionYears, policy.minimumYears, 1)}
                      className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-all"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    {isDirty && (
                      <button
                        onClick={() => saveRetention(policy.configKey)}
                        disabled={isSaving}
                        className="ml-1 flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-bold hover:bg-primary/90 disabled:opacity-50 transition-all"
                      >
                        {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save
                      </button>
                    )}
                    {isSaved && (
                      <span className="ml-1 flex items-center gap-1 text-xs font-bold text-emerald-600">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataManagement;
